import React, { useState, useEffect, useRef, useContext } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { INTELLIMIZE_COUPON_EVENT } from '../../../constants/defaults';
import { useDispatch } from 'react-redux';
import TextMaskCustom from '../../static/TextMaskCustom';
import { AuthContext } from '../../../Contexts';
import { modalActions } from '../../../reducers/modalSlice';
import { newSendCouponToMedia } from '../../../services/Api/Coupon/CouponService';

const SendContact = ({
  isEmail = false,
  drugData,
  selectedPrice,
  selectedPharmacyName = '',
  emailButtonClicked = false,
  doNotMarket = false,
  selectedDisplayQuantity,
  handleCloseBottomSheetModal = () => {},
  contactSent = false,
  updateContactSent = () => {},
  eventObj = {},
  sentMessage = '',
  sendText = ''
}) => {
  const [inputValue, setInputValue] = useState('');
  const [invalidInput, setInvalidInput] = useState(false);
  const [apiLoading, setApiLoading] = useState(false);
  const input = useRef();
  const inputText = useRef();
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  let lang = i18n.language;
  const userData = useContext(AuthContext)?.userData;
  const isUserLogin = userData?.isLoggedIn;
  const invalidChars = ['-', '+', 'e', '.'];

  const sendContactOnMedia = async (isEmail, inputValue) => {
    let res;
    try {
      const discount = selectedPrice.LoyaltyDiscounts;
      let loyaltyDiscountType, loyaltyDiscountAmount;
      loyaltyDiscountType = loyaltyDiscountAmount = '';
      if (!_.isEmpty(discount)) {
        loyaltyDiscountType = discount[0].discountType;
        loyaltyDiscountAmount = discount[0].discountAmount;
      }
      let textValue = !isEmail ? inputValue.replace(/\D/g, '') : inputValue;
      res = await newSendCouponToMedia(
        selectedPrice.PartnerUser,
        selectedPrice.PartnerPassword,
        doNotMarket ? 'Your Prescription' : drugData.name,
        selectedPharmacyName,
        drugData.form,
        drugData.dosage,
        selectedDisplayQuantity,
        selectedPrice.LoyaltyPrice,
        isEmail ? 'Email' : 'Text',
        textValue.trim(),
        false,
        drugData.seoName,
        selectedPrice.Price,
        selectedPrice.LoyaltyApplicable,
        selectedPrice.LoyaltyExcluded,
        selectedPrice.LoyaltyBonusSavings,
        loyaltyDiscountType,
        loyaltyDiscountAmount,
        isUserLogin,
        drugData.ndc,
        selectedPrice.preferredNdc
      );
      if (res && res.data && res.data.Result) {
        let result = res.data.Result;
        let finalEventObj = {
          event: isEmail ? 'couponEmailSendSuccess' : 'couponTextSendSuccess',
          formId: isEmail ? 'email' : 'text',
          ...eventObj,
          authNumber: result.MemberNumberFull,
          grpNumber: result.GroupNumber,
          bin: result.BIN,
          pcn: result.PCN
        };
        setInputValue('');
        _.triggerGTMVirtualPageViewEvent(finalEventObj);
        _.sendIntellimizeEvent(
          isEmail
            ? INTELLIMIZE_COUPON_EVENT.COUPON_EMAIL_SENT
            : INTELLIMIZE_COUPON_EVENT.COUPON_TEXT_SENT
        );
      }
      return res;
    } catch (err) {
      console.log('err in sendContactOnMedia function', err);
    }
  };

  const updateInput = (event = { target: { value: '' } }) => {
    const { value } = event.target;
    if (!value) setInputValue(value);
    if (!isEmail && invalidChars.indexOf(value) !== -1) {
      return;
    }
    setInputValue(value);
    // const re = /^[0-9\b]+$/;
    if (
      !isEmail &&
      event.target.value.length !== 14 &&
      !invalidChars.includes(event.target.value)
    ) {
      return;
    }

    setInputValue(value);
    setInvalidInput(false);
  };

  const validateInput = () => {
    if (isEmail) {
      if (inputValue !== '') {
        if (!_.validateEmail(inputValue)) {
          setInvalidInput(true);
          return false;
        } else {
          setInvalidInput(false);
          return true;
        }
      }
      setInvalidInput(true);
      return false;
    } else if (inputValue.length !== 14) {
      setInvalidInput(true);
      return false;
    }
    return true;
  };

  const postContactAPI = async () => {
    await setApiLoading(true);
    try {
      let contactResponse = await sendContactOnMedia(
        isEmail,
        inputValue.trim()
      );
      if (contactResponse?.response?.status !== 400)
        console.log('contactResponse?.response', contactResponse?.response);

      await updateContactSent(true);
      if (isEmail) {
        dispatch(modalActions.couponEmailSentSignupModal());
      } else {
        dispatch(modalActions.couponTextSentSignupModal());
      }
    } catch (err) {
      console.log(err);
    } finally {
      await setApiLoading(false);
    }
  };

  const handleKeydownEvent = (event = { preventDefault() {} }) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      const isInputValid = validateInput();
      if (isInputValid) postContactAPI();
      return;
    }
  };

  const handleSubmit = (event = { preventDefault() {} }) => {
    event.preventDefault();
    const isInputValid = validateInput();
    if (isInputValid) postContactAPI();
  };

  useEffect(() => {
    setInvalidInput(false);
    setInputValue('');
    const resetSuccessPopup = async () => {
      document
        .getElementById('sendContactField')
        ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      input.current && input.current.focus();
    };
    resetSuccessPopup();
    if (isUserLogin && isEmail) {
      setInputValue(userData && userData.email);
    }
  }, [isEmail, emailButtonClicked]);

  useEffect(() => {
    if (!invalidInput) {
      input.current && input.current.focus();
    }
  }, [invalidInput]);

  useEffect(() => {
    const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
    const isOldiOS = (() => {
      const match = navigator.userAgent.match(/OS (\d+)_/);
      return match ? parseInt(match[1]) < 15 : false;
    })();

    const getActualInput = () => {
      if (isEmail) return input.current;
      return inputText.current?.inputElement || inputText.current;
    };

    const actualInput = getActualInput();

    const tryFocus = () => {
      const el = getActualInput();
      const isIOS = /iPhone|iPad|iPod/.test(navigator.userAgent);
      if (!el) return;
      if (isIOS) {
        // Use requestAnimationFrame + timeout to ensure DOM & keyboard settle
        requestAnimationFrame(() => {
          setTimeout(() => {
            el.focus();

            // iOS Safari workaround: manual scroll using scrollTop
            el.scrollIntoView({
              behavior: 'auto', // or 'smooth'
              block: 'center'
            });

            // Fallback for some cases where scrollIntoView fails:
            if (typeof el.getBoundingClientRect === 'function') {
              const elRect = el.getBoundingClientRect();
              const offset = window.innerHeight / 2 - elRect.height / 2;
              window.scrollTo({
                top: window.scrollY + elRect.top - offset,
                behavior: 'smooth'
              });
            }
          }, 100); // 100-300ms usually works best
        });
      } else {
        // Non-iOS: simpler logic
        el.focus();
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 50);
      }
    };

    if (isIOS && isOldiOS) {
      const gestureHandler = () => {
        tryFocus();
      };

      // Touch fallback on actual input
      actualInput?.addEventListener?.('touchstart', gestureHandler, {
        once: true,
        passive: true
      });

      // Optional: tap anywhere
      document.body.addEventListener('touchend', gestureHandler, {
        once: true,
        passive: true
      });

      return () => {
        actualInput?.removeEventListener?.('touchstart', gestureHandler);
        document.body.removeEventListener('touchend', gestureHandler);
      };
    } else {
      // Newer iOS or other devices
      setTimeout(() => {
        tryFocus();
      }, 30);
    }
  }, [isEmail]);

  return (
    <div>
      {!contactSent ? (
        <div className="modalWrapper">
          <h3 id="send-contact-field">
            {isEmail
              ? t('Enter the email where you’d like ')
              : t('Enter your phone number below ')}
          </h3>
          <form autoComplete="on">
            <div className="form-control">
              <div className="fullwidth mobileWidth">
                {isEmail ? (
                  <input
                    id="sendContactField"
                    type={'email'}
                    value={inputValue}
                    onChange={updateInput}
                    className={`themeFocus ${
                      invalidInput ? 'error' : ''
                    } dialogComponent`}
                    placeholder={'johnsmith@email.com'}
                    ref={input}
                    onBlur={validateInput}
                    onKeyDown={handleKeydownEvent}
                    aria-labelledby="send-contact-field"
                    aria-required="true"
                    autoComplete={'email'}
                    aria-invalid={invalidInput ? 'true' : 'false'}
                    aria-describedby="invalidInputErr"
                    inputMode={'email'}
                  />
                ) : (
                  <TextMaskCustom
                    id="sendContactField"
                    type={'text'}
                    value={inputValue}
                    onChange={updateInput}
                    className={`themeFocus ${
                      invalidInput ? 'error' : ''
                    } dialogComponent`}
                    placeholder={'(123) 456-7890'}
                    ref={inputText}
                    onBlur={validateInput}
                    onKeyDown={handleKeydownEvent}
                    aria-labelledby="send-contact-field"
                    aria-required="true"
                    autoComplete={'tel-national'}
                    aria-invalid={invalidInput ? 'true' : 'false'}
                    aria-describedby="invalidInputErr"
                    inputMode={'numeric'}
                  />
                )}
                {invalidInput && (
                  <span
                    id="invalidInputErr"
                    className="label-error"
                    role="alert"
                  >
                    {t('Please enter a valid')}&nbsp;
                    {isEmail ? t('email address.') : t('phone number.')}
                  </span>
                )}
              </div>
            </div>
            <div>
              <p className="send-terms">
                {isEmail
                  ? `${t('By clicking')} "${
                      !_.isEmpty(sendText) ? sendText : t('Send')
                    }", ${t('I agree')}`
                  : !_.isEmpty(sendText)
                  ? 'By clicking “Send coupon”, I agree to receive periodic marketing and informational automated messages from SingleCare. '
                  : t('T-and-C text1')}
                {!isEmail && t('See')}&nbsp;
                <a
                  href={_.withLocale(lang, '/terms-and-conditions')}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="dialogComponent"
                >
                  {t('Terms & Conditions Text')}&nbsp;
                </a>
                {i18n.language === 'es' && isEmail ? ' de SingleCare' : ''}
                .&nbsp;
                {!isEmail &&
                  `${t('Text STOP to quit.')} ${t(
                    'Message & data rates may apply.'
                  )}`}
              </p>
            </div>
            {!apiLoading ? (
              <button
                className={`button pink submitAction submit-button themeFocus dialogComponent ${
                  isEmail ? 'email-send' : 'text-send'
                }`}
                onClick={handleSubmit}
                disabled={
                  isEmail && inputValue != ''
                    ? !_.validateEmail(inputValue)
                    : inputValue?.length !== 14
                }
              >
                Send
              </button>
            ) : (
              <div className="loaderDiv">
                <img
                  src="/content/images/loader/loading.gif"
                  alt="loader"
                  className="sendLoader"
                />
              </div>
            )}
          </form>
        </div>
      ) : (
        <div className="successMessage">
          <div className="successMessageContainer">
            <span className="cardSendTitle" role="status">
              {t(sentMessage)}
            </span>
            <button
              className={`button pink successButton themeFocus dialogComponent`}
              onClick={handleCloseBottomSheetModal}
            >
              Okay
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SendContact;
